import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import useInput from 'lib/hooks/useInput';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import { employee_add } from 'components/base/images';
import CustomSpan from 'components/common/text/CustomSpan';
import EmployeeCard from 'components/account/EmployeeCard';
import EmployeeModalContainer from 'containers/account/EmployeeModalContainer';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import { EmployeeActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import { useShallowAppSelector } from 'store/hooks';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import CircularLoading from 'components/base/loading/CircularLoading';
import { IEmployeeInfo } from 'lib/types/accountTypes';
import CustomPagination from 'components/common/pagination/CustomPagination';
import { PageSubTitle } from 'components/base/title/PageTitle';
import { useTranslation } from 'react-i18next';
import ListEmpty from 'components/common/list/ListEmpty';
interface EmployeeInfoProps {}

function EmployeeContainer({}: EmployeeInfoProps) {
  const {
    fetchEmployeeList: { list: employeeList, pagingData: employeeListPage },
    fetchEmployeeListSuccess,
    fetchEmployeeListPending,
    fetchEmployeeListFailure,
  }: {
    fetchEmployeeList: { list: IEmployeeInfo[]; pagingData: OffsetPagingData };
    fetchEmployeeListSuccess: boolean;
    fetchEmployeeListPending: boolean;
    fetchEmployeeListFailure: boolean;
  } = useShallowAppSelector(state => ({
    fetchEmployeeList: state.employee.employeeList.data || {},
    fetchEmployeeListSuccess: state.employee.employeeList.success,
    fetchEmployeeListPending: state.employee.employeeList.pending,
    fetchEmployeeListFailure: state.employee.employeeList.failure,
  }));
  const { t } = useTranslation();
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const page = useInput(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const selectedEmployeeNumber = useInput('');
  const handleCloseModal = () => {
    setIsModalOpen(false);
    page.setValue(1);
    EmployeeActions.fetch_employee_list_request({ page: page.value });
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleChangePage = (value: number) => {
    page.setValue(value);
  };

  const handleRefreshList = () => {
    EmployeeActions.fetch_employee_list_request({ page: page.value });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    // init
    EmployeeActions.fetch_employee_list_request({ page: page.value });
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useDidUpdateEffect(() => {
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
    EmployeeActions.fetch_employee_list_request({ page: page.value });
  }, [page.value]);

  useDidUpdateEffect(() => {
    if (!fetchEmployeeListSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchEmployeeListSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchEmployeeListFailure) return;
    setIsLoadFailure(true);
  }, [fetchEmployeeListFailure]);

  return (
    <StyledEmployee data-component-name="Employee">
      {useMemo(
        () => (
          <PlainModal isOpen={isModalOpen} onClose={handleCloseModal} width={1160}>
            <AppModal
              title={'Employee'}
              content={<EmployeeModalContainer selectedEmployeeNumber={selectedEmployeeNumber} />}
              contentCardStyle={{
                padding: '0 0 28px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={handleCloseModal}
              onCancel={handleCloseModal}
              hideButton={true}
            />
          </PlainModal>
        ),
        [isModalOpen, selectedEmployeeNumber.value],
      )}
      <div className="employee__container">
        <PageSubTitle title={t('TITLE_EMPLOYEE_LIST')} />
        <div className="employee__content_box ">
          <div className="employee__content_button_box ">
            {employeeList?.length > 0 && (
              <MuiButton
                disableElevation
                className="employee__content_add"
                variant="contained"
                onClick={() => {
                  handleOpenModal();
                  selectedEmployeeNumber.setValue('');
                }}
              >
                <CustomSpan fontSize={13} fontWeight={400}>
                  {'+ '}
                  <T>ACCOUNT_ADDING_EMPLOYEE</T>
                </CustomSpan>
              </MuiButton>
            )}
          </div>
          <div className="employee__list_box">
            {!isLoadSuccess && fetchEmployeeListPending && (
              <div className="infinite-scroll-loading">
                <CircularLoading />
              </div>
            )}
            {useMemo(
              () => (
                <>
                  {!!isLoadSuccess &&
                    (employeeList?.length > 0 ? (
                      employeeList.map((item: IEmployeeInfo, idx: number) => {
                        return (
                          <div
                            onClick={() => {
                              handleOpenModal();
                              selectedEmployeeNumber.setValue(item.syncUid);
                            }}
                            key={idx}
                          >
                            <EmployeeCard item={item} />
                          </div>
                        );
                      })
                    ) : (
                      <div
                        className="employee__list_empty_box "
                        onClick={() => {
                          handleOpenModal();
                          selectedEmployeeNumber.setValue('');
                        }}
                      >
                        <img src={employee_add} alt="employee_add" />
                        <p>
                          <CustomSpan fontSize={16} marginRight={10} fontColor="#33B5E4">
                            +
                          </CustomSpan>
                          <CustomSpan fontSize={19} fontWeight={500} marginRight={10}>
                            <T>ACCOUNT_ADDING_EMPLOYEE</T>
                          </CustomSpan>
                        </p>
                      </div>
                    ))}
                </>
              ),
              [page.value, employeeList, isLoadSuccess],
            )}
            {!!isLoadFailure && (
              <ListEmpty
                text="GLOBAL_LIST_ERROR"
                isRefreshButton={true}
                onRefresh={handleRefreshList}
              />
            )}
          </div>
          <div className="employee__list_paging ">
            {!!employeeListPage?.totalPage && (
              <CustomPagination
                totalPage={employeeListPage?.totalPage}
                currPage={page.value}
                onChange={handleChangePage}
              />
            )}
          </div>
        </div>
      </div>
    </StyledEmployee>
  );
}

const StyledEmployee = styled.section<{}>`
  .employee__container {
    flex-wrap: wrap;
    margin-bottom: 90px;

    .employee__title_box {
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      .employee__title_icon {
        width: 60px;
        height: 60px;
      }
    }

    .employee__content_box {
      position: relative;
      .employee__content_button_box {
        position: absolute;
        top: -70px;
        right: 0;
        .employee__content_add {
          width: 300px;
          height: 40px;
          border-radius: 20px;
          background-color: #00a4e3;
          box-shadow: inset 3px 3px 6px rgb(0 0 0 / 16%);
        }
      }
      .employee__list_box {
        display: flex;
        flex-wrap: wrap;
        column-gap: 27px;
        row-gap: 25px;
        height: 625px;
        width: 100%;
        position: relative;

        .employee__list_empty_box {
          border: 1px solid #33b5e4;
          border-radius: 10px;
          width: 300px;
          height: 300px;
          padding: 45px 35px 40px;
          text-align: center;
          position: relative;
          p {
            margin-top: 10px;
          }
          background-color: #fff;
          &:hover {
            background-color: #f4f4f4;
            cursor: pointer;
          }
        }
      }
      .employee__list_paging {
        width: 100%;
        margin-top: 50px;
      }
    }
  }
`;

export default React.memo(EmployeeContainer);
